import { ClipboardPaste } from 'lucide-react';
import { useState } from 'react';
import { Button } from './button.js';

/// Cola o texto da área de transferência no campo, com um toque.
///
/// No celular não existe Ctrl+V: colar exige segurar o dedo no campo e acertar o menu do sistema,
/// que muda a cada aparelho. Um botão resolve — e é justamente no celular que se cola linha
/// digitável, vindo do aplicativo do banco que acabou de ser fechado.
///
/// O atalho do teclado continua valendo em quem tem teclado; este botão é um caminho a mais, nunca
/// o único.
export function PasteButton({
  label = 'Colar',
  onPaste,
  onError,
  disabled,
}: {
  label?: string;
  onPaste: (text: string) => void;
  /// Chamado quando o navegador nega a leitura ou não há texto copiado. Quem chama decide onde a
  /// mensagem aparece — o botão não tem lugar bom para exibi-la sem empurrar o campo ao lado.
  onError?: ((message: string) => void) | undefined;
  disabled?: boolean | undefined;
}) {
  const [busy, setBusy] = useState(false);

  async function paste() {
    setBusy(true);
    try {
      const text = (await navigator.clipboard.readText()).trim();
      if (text === '') {
        onError?.('Não há texto copiado na área de transferência.');
        return;
      }
      onPaste(text);
    } catch {
      // Permissão negada, navegador sem suporte, contexto inseguro: digitar e colar pelo teclado
      // continuam valendo, então isto é um aviso, não uma falha da tela.
      onError?.('Não foi possível ler a área de transferência. Cole com o teclado.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Button
      type="button"
      variant="outline"
      loading={busy}
      disabled={disabled}
      onClick={() => void paste()}
    >
      {busy ? null : <ClipboardPaste className="size-4" />}
      {label}
    </Button>
  );
}
